/**
 * Render-only SATELLITES of a spatial-index entity — the shared mechanism
 * (S9b, MLEADER block content; generalised for INSERT / `block-insert`
 * wrappers, see `./liftBlockInsert.ts`) for "one spatial-index entity → many
 * render-only child renderables".
 *
 * A satellite is a first-class per-kind renderable (line / hatch / text /
 * …) that draws through the normal bucket / fill / text pipelines but is
 * NEVER registered in the spatial index: the PARENT stays the single
 * pickable / selectable entity (its bbox covers the children). Because
 * satellites are absent from the spatial index they must NOT be tracked as
 * independent `liftedEntityIds` — the per-frame viewport-cull diffs that Set
 * against a spatial-index-derived visible set (`computeVisibleSet`) and
 * would zero-fill them on the first frame, never able to re-lift them
 * (`liftRenderableForId` has no stub for a synthetic child id). Instead the
 * cull adds / removes them in LOCKSTEP with their parent through
 * {@link collectRenderSatellites} — the single truth for "which satellites
 * ride with this parent" — consumed by:
 *
 *   - {@link expandRenderSatellites} — the bulk-load RENDER feed expansion
 *     (`finishSceneBulkLoad`), so each child gets its own line bucket /
 *     fill / text mesh;
 *   - {@link populateLiftedAndBlockChildren} — the lifted-set / satellite
 *     split the cull relies on (`multileaderBlockChildrenRef`);
 *   - `FrameLoops` Pass 1 (remove satellites with a culled parent) and
 *     Pass 2 (re-expand them on relift).
 *
 * Two parent kinds carry satellites:
 *
 *   1. `multileader` — the `block-content` primitive's `flattened` children.
 *      AutoCAD treats a MULTILEADER as a SINGLE object on its OWN layer, so
 *      every child is RE-HOMED onto the parent's layer here (see the inline
 *      comment).
 *   2. `block-insert` — the INSERT wrapper's `childRenderables` (built by
 *      `liftBlockInsertRenderable`). An INSERT's children KEEP the layer the
 *      typed flatten resolved (AutoCAD: only layer-"0" children follow the
 *      INSERT's layer; every other child is governed by its own layer), so
 *      they are returned as-is — they already carry `renderParentId`.
 *
 * # Nesting (Codex PR #784 round 3 P1)
 *
 * A satellite may itself be a CARRIER — a cache-direct INSERT whose definition
 * holds an MLEADER with block content lifts to a wrapper whose immediate child
 * is that flattened MLEADER. {@link collectRenderSatellites} therefore expands
 * carriers RECURSIVELY, returning ONE FLAT list in which every descendant is
 * attributed to the OUTER spatial-index parent (`renderParentId`). Because
 * every consumer above goes through that single collector, the bucket feed,
 * the direct-mesh emitters, the lifted-set split and the cull's remove / re-add
 * all treat the whole subtree as one lockstep unit. The non-nested case (no
 * carrier among the direct children — the overwhelmingly common one) still
 * returns the direct array verbatim, with zero extra allocation.
 *
 * ## Why the walk terminates (Codex PR #784 round 7 P2)
 *
 * The tree handed to the collector has ALREADY been MATERIALISED by the typed
 * flatten (`adapters/typed/blockFromEntity.ts`, which permits
 * `MAX_TYPED_BLOCK_RECURSION_DEPTH = 50` nested block levels): every carrier's
 * children are real, finite arrays that exist in memory before this walk
 * starts. So the walk is bounded by the materialised node count — no numeric
 * depth cap is needed, and the walk visits EVERY level the flatten produced.
 *
 * The ONLY unbounded shape is a CYCLE, which the flatten can never emit (each
 * flattened child gets a fresh path-prefixed id, `${parentEntityId}::${child
 * .id}-${i}`) but a hand-built / malformed payload could. That is cut by an
 * ANCESTOR-PATH guard: a carrier is expanded only when its id is not already on
 * the path from the outer parent down to it, and the id is removed again when
 * that branch is done. Every recursive step therefore adds a NEW id to a set
 * bounded by the distinct ids in a finite, materialised tree → the recursion
 * ends. Path-scoped (not a global `seen`) is load-bearing: a global visited set
 * would silently DROP the descendants of a legal SIBLING carrier that happens
 * to repeat an id, reintroducing exactly the truncation this section rules out.
 *
 * A fixed `MAX_SATELLITE_DEPTH = 8` used to bound the walk instead. It sat far
 * BELOW the flatten's own 50-level limit, so a legal nine-level alternating
 * INSERT / block-content-MLEADER chain — already fully materialised by the
 * flatten — was appended without its deeper descendants, and that block
 * graphics silently vanished from both the bulk-load feed and every relift.
 *
 * Extracted from `sceneBulkLoadHelpers.ts` (size cap) when the collector was
 * generalised; no behaviour change for the MLEADER path.
 */

import type { RenderableEntity } from "../types";

/**
 * Expand each satellite-carrying renderable (MLEADER block content, INSERT
 * `block-insert` wrapper) into its child renderables, appended immediately
 * after the parent, so they flow through the normal per-kind line / fill /
 * text pipelines exactly like a top-level INSERT's flattened children on the
 * legacy branch. Every other renderable passes through unchanged.
 *
 * The children are added ONLY to the RENDER feed passed here — never to the
 * spatial index (the caller bulk-loads the spatial index from the un-expanded
 * list BEFORE calling `finishSceneBulkLoad`), so the parent stays the single
 * pickable / selectable entity while its content is drawn with full per-child
 * color / linetype / kind fidelity (each child gets its own line bucket /
 * fill / text mesh).
 *
 * Returns the input array unchanged (no allocation) when no renderable in the
 * feed carries satellites.
 */
export function expandRenderSatellites(
  renderables: RenderableEntity[],
): RenderableEntity[] {
  let hasSatellites = false;
  for (const r of renderables) {
    if (carriesRenderSatellites(r)) {
      hasSatellites = true;
      break;
    }
  }
  if (!hasSatellites) return renderables;

  const out: RenderableEntity[] = [];
  for (const r of renderables) {
    out.push(r);
    for (const child of collectRenderSatellites(r)) out.push(child);
  }
  return out;
}

/**
 * Return a renderable's render-only satellites (the single doc-truth for
 * "which render-only children ride with this parent" — see the module doc).
 *
 *   - `block-insert` wrapper → its `childRenderables` verbatim (each already
 *     carries `renderParentId = wrapper.id` and its own resolved layer — set
 *     by `liftBlockInsertRenderable`). The legacy branch's InstancedMesh
 *     wrapper stubs carry an EMPTY `childRenderables` (their geometry is
 *     drawn by the InstancedMesh), so they yield no satellites.
 *   - `multileader` → its `block-content` flattened children, re-homed onto
 *     the parent layer + attributed to the parent (details inline).
 *   - anything else → `[]`.
 *
 * Any of those children that is ITSELF a carrier is expanded in turn (module
 * doc "Nesting"), so the result is the parent's WHOLE render-only subtree,
 * flat, every entry attributed to `r` — never a partial first level.
 */
export function collectRenderSatellites(
  r: RenderableEntity,
): RenderableEntity[] {
  const direct = collectDirectRenderSatellites(r);
  if (direct.length === 0) return direct;
  // Fast path — no satellite CARRIER among the direct children (the
  // overwhelmingly common case: plain per-kind geometry). The direct list is
  // returned verbatim, so a `block-insert` wrapper still hands back its own
  // `childRenderables` array with zero allocation.
  let hasNested = false;
  for (const child of direct) {
    if (carriesRenderSatellites(child)) {
      hasNested = true;
      break;
    }
  }
  if (!hasNested) return direct;
  const out: RenderableEntity[] = [];
  appendSatellitesDeep(direct, r.id, out, new Set<string>([r.id]));
  return out;
}

/**
 * `true` when `r` carries render-only satellites of its own — the
 * allocation-free twin of {@link collectDirectRenderSatellites}, used to decide
 * whether the expansion has to walk deeper (and by `expandRenderSatellites`'s
 * identity pre-scan).
 */
function carriesRenderSatellites(r: RenderableEntity): boolean {
  const geom = r.geometry;
  if (geom.kind === "block-insert") return geom.childRenderables.length > 0;
  if (geom.kind !== "multileader") return false;
  for (const prim of geom.primitives) {
    if (prim.kind === "block-content" && prim.flattened.length > 0) return true;
  }
  return false;
}

/**
 * Append `children` and — RECURSIVELY — the satellites of any child that is
 * itself a satellite CARRIER (Codex PR #784 round 3 P1 — "expand satellite
 * carriers recursively").
 *
 * Why it is needed: a cache-direct INSERT whose definition contains an MLEADER
 * with block content lifts to a `block-insert` wrapper whose immediate child is
 * the flattened MLEADER. Appending that child alone leaves the MLEADER's own
 * embedded block graphics out of the bucket feed AND out of the direct-mesh
 * emitters, on bulk-load and after every relift — the legacy branch only works
 * because there the flattened MLEADER is a TOP-LEVEL feed entry that
 * `expandRenderSatellites` visits in its own right.
 *
 * `outerParentId` is the OUTER spatial-index entity (the INSERT), and every
 * grandchild is re-attributed to it: `renderParentId` drives the per-viewport
 * visibility mask (which only keeps ids the spatial index knows) and the
 * lifted-set / satellite split, both of which must key on the one pickable
 * ancestor — never on an intermediate satellite that is itself absent from the
 * index. The grandchild's own `id`, `layer` and `renderParentLayers` are
 * untouched (bucket slots and the layer-chain gate key by them).
 *
 * `ancestorIds` is the id PATH from the outer parent down to the carrier whose
 * `children` these are — the walk's ONLY bound (module doc "Why the walk
 * terminates"). A carrier already on that path closes a CYCLE: it is appended
 * but NOT expanded. Every other level the flatten materialised is walked in
 * full, however deep. The id is removed again on the way out so a legal SIBLING
 * branch repeating it still expands (a global visited set would truncate it).
 */
function appendSatellitesDeep(
  children: readonly RenderableEntity[],
  outerParentId: string,
  out: RenderableEntity[],
  ancestorIds: Set<string>,
): void {
  for (const child of children) {
    out.push(child);
    if (!carriesRenderSatellites(child)) continue;
    if (ancestorIds.has(child.id)) continue;
    ancestorIds.add(child.id);
    const grandchildren = collectDirectRenderSatellites(child).map((g) =>
      g.renderParentId === outerParentId
        ? g
        : { ...g, renderParentId: outerParentId },
    );
    appendSatellitesDeep(grandchildren, outerParentId, out, ancestorIds);
    ancestorIds.delete(child.id);
  }
}

/**
 * One LEVEL of satellites — the per-kind rule, without the recursion
 * {@link collectRenderSatellites} wraps around it.
 */
function collectDirectRenderSatellites(
  r: RenderableEntity,
): RenderableEntity[] {
  if (r.geometry.kind === "block-insert") return r.geometry.childRenderables;
  if (r.geometry.kind !== "multileader") return [];
  const out: RenderableEntity[] = [];
  for (const prim of r.geometry.primitives) {
    if (prim.kind === "block-content") {
      for (const child of prim.flattened) {
        // FIX (Codex S9b P2 — "Hide expanded block content with the MLEADER"):
        // AutoCAD treats a MULTILEADER as a SINGLE object on its OWN layer —
        // hiding the MLEADER's layer hides the ENTIRE callout, block content
        // included. The flatten resolves each child's BYLAYER colour/linetype
        // against the block's sub-entity layer and BAKES it into `colorHex` /
        // `linetype`, but the child's `layer` field ALSO drives batched-bucket
        // grouping + layer-visibility. Left as the sub-entity layer, hiding the
        // parent MLEADER layer would strip the leader/arrow yet leave the block
        // graphics floating (they sit in a bucket keyed by the child layer).
        // Re-home each child onto the PARENT MLEADER's layer (a shallow copy —
        // never mutate the shared `prim.flattened` objects) so the whole
        // callout hides in lockstep, while the already-resolved colour/linetype
        // keep full per-child fidelity (colour baked at flatten; linetype baked
        // by `bakeChildByLayerLinetype` before this re-home — see
        // `multileaderGeometry.ts`).
        //
        // FIX (Codex S9b P2 — "Attribute direct block-content meshes to the
        // parent"): a child that renders as a DIRECT mesh (TEXT / HATCH /
        // WIPEOUT) stamps `userData.entityId` for the per-viewport visibility
        // mask (`applySceneVisibilityMask`), which only KEEPS ids in the
        // spatial-index-derived visible set. These children are intentionally
        // absent from the spatial index (the MLEADER is the single pickable
        // entity), so under their OWN id they'd be masked OUT of every VP pass
        // even when the parent MLEADER is visible. Carry `renderParentId = r.id`
        // so the direct-mesh factories stamp the PARENT's id (mirroring the
        // synthetic MLEADER/dimension label path) — the mask then keeps them
        // whenever the parent is visible. The child's OWN `id` is untouched, so
        // batched-line bucket slots / satellite tracking still key by child id.
        //
        // Codex PR #784 round 3 P1 — the re-home also carries the parent's own
        // `renderParentLayers`: when the MLEADER is itself a block child (an
        // INSERT's satellite), its content now sits on the MLEADER's layer and
        // must hide with the ENCLOSING references too. Undefined for a
        // top-level MLEADER, so the field simply stays absent there.
        out.push({
          ...child,
          layer: r.layer,
          renderParentId: r.id,
          ...(r.renderParentLayers === undefined
            ? {}
            : { renderParentLayers: r.renderParentLayers }),
        });
      }
    }
  }
  return out;
}

/**
 * SINGLE SOURCE OF TRUTH for the bulk-load's lifted-set / satellite split.
 * Repopulates the two structures the per-frame viewport-cull relies on, in
 * place, from the UN-EXPANDED top-level `renderables` (the same list the
 * caller bulk-loaded into the spatial index):
 *
 *   - `liftedIds` — every top-level entity id (each has a spatial-index stub).
 *     The cull diffs this against a spatial-index-derived visible set, so ONLY
 *     entities the index knows about may live here.
 *   - `blockChildrenByParent` — parent id (MLEADER with block content, or an
 *     INSERT `block-insert` wrapper) → its satellite children. These children
 *     are drawn as first-class bucket / fill / text entities but are ABSENT
 *     from the spatial index (the parent is the single pickable entity).
 *     Tracking them in `liftedIds` is the S9b data-loss bug: the cull would
 *     zero-fill them on the first frame (they never appear in any
 *     `computeVisibleSet` result) and could never re-lift them
 *     (`liftRenderableForId` has no stub for a synthetic child id). Instead the
 *     cull adds / removes them in lockstep with the parent using this map.
 *
 * Both structures are cleared first so a prior document / pass cannot leak.
 * Writing in place (vs returning fresh collections) keeps the per-pass cost at
 * one O(n) walk with no intermediate allocation on the huge cache-direct feed.
 */
export function populateLiftedAndBlockChildren(
  renderables: RenderableEntity[],
  liftedIds: Set<string>,
  blockChildrenByParent: Map<string, RenderableEntity[]>,
): void {
  liftedIds.clear();
  blockChildrenByParent.clear();
  for (const r of renderables) {
    liftedIds.add(r.id);
    const children = collectRenderSatellites(r);
    if (children.length > 0) blockChildrenByParent.set(r.id, children);
  }
}
